import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="expandable-table-rows"
// Handles expanding/collapsing table rows with nested content
// Supports "Expand All" / "Collapse All" functionality
export default class extends Controller {
  static targets = ["expandAllLink", "collapseAllLink", "expandableRow", "childrenRow"]

  connect() {
    // Start with all rows expanded
    this.expandableRowTargets.forEach((row) => {
      this.expandRow(row);
    });
    this.updateLinksVisibility();
  }

  // Turbo Stream row replacement (e.g. Provider Intelligence filtering, bulk edit,
  // publish) swaps the <tbody> contents without disconnecting/reconnecting this
  // controller, since the controller is scoped to a wrapping element that survives
  // the swap. Stimulus still fires this target-lifecycle callback for every newly
  // connected expandableRow, so recompute link visibility whenever the row set
  // changes underneath us — otherwise a stale "Expand all"/"Collapse all" link can
  // linger after a refresh (e.g. all-collapsed rows re-render already-expanded per
  // their default markup, but the link still says "Expand all").
  expandableRowTargetConnected() {
    this.updateLinksVisibility();
  }

  expandableRowTargetDisconnected() {
    this.updateLinksVisibility();
  }

  expandAll(e) {
    e.preventDefault();
    this.expandableRowTargets.forEach((row) => {
      this.expandRow(row);
    });
    this.updateLinksVisibility();
  }

  collapseAll(e) {
    e.preventDefault();
    this.expandableRowTargets.forEach((row) => {
      this.collapseRow(row);
    });
    this.updateLinksVisibility();
  }

  toggleRow(e) {
    const row = e.currentTarget.closest("[data-expandable-table-rows-target='expandableRow']");
    if (row.dataset.expanded === "true") {
      this.collapseRow(row);
    } else {
      this.expandRow(row);
    }
    this.updateLinksVisibility();
  }

  expandRow(row) {
    const rowId = row.dataset.rowId;
    const childrenRows = this.childrenRowTargets.filter(r => r.dataset.parentRowId === rowId);

    childrenRows.forEach((childRow) => {
      childRow.classList.remove("hidden");
    });

    // Toggle chevron icons
    const downChevron = row.querySelector("[data-chevron='down']");
    const upChevron = row.querySelector("[data-chevron='up']");
    if (downChevron) downChevron.classList.add("hidden");
    if (upChevron) upChevron.classList.remove("hidden");

    row.dataset.expanded = "true";
  }

  collapseRow(row) {
    const rowId = row.dataset.rowId;
    const childrenRows = this.childrenRowTargets.filter(r => r.dataset.parentRowId === rowId);

    childrenRows.forEach((childRow) => {
      childRow.classList.add("hidden");
    });

    // Toggle chevron icons
    const downChevron = row.querySelector("[data-chevron='down']");
    const upChevron = row.querySelector("[data-chevron='up']");
    if (downChevron) downChevron.classList.remove("hidden");
    if (upChevron) upChevron.classList.add("hidden");

    row.dataset.expanded = "false";
  }

  updateLinksVisibility() {
    const hasExpandableRows = this.expandableRowTargets.length > 0;
    const allExpanded = this.expandableRowTargets.every(row => row.dataset.expanded === "true");
    const allCollapsed = this.expandableRowTargets.every(row => row.dataset.expanded !== "true");

    if (this.hasExpandAllLinkTarget && this.hasCollapseAllLinkTarget) {
      if (!hasExpandableRows) {
        this.expandAllLinkTarget.classList.add("hidden");
        this.collapseAllLinkTarget.classList.add("hidden");
      } else if (allExpanded) {
        this.expandAllLinkTarget.classList.add("hidden");
        this.collapseAllLinkTarget.classList.remove("hidden");
      } else if (allCollapsed) {
        this.expandAllLinkTarget.classList.remove("hidden");
        this.collapseAllLinkTarget.classList.add("hidden");
      } else {
        // Some expanded, some collapsed - show both or show the one that makes more sense
        this.expandAllLinkTarget.classList.remove("hidden");
        this.collapseAllLinkTarget.classList.add("hidden");
      }
    }
  }
}

